import type {
  DashboardStats,
  LyricsResult,
  MobileGenerationRecord,
  MobileUser,
  NeteaseReleaseDraft,
  Track,
} from "@/types";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  const payload = (await response.json().catch(() => null)) as
    | (T & { error?: string })
    | null;

  if (!response.ok) {
    throw new Error(payload?.error || `请求失败（${response.status}）`);
  }
  return payload as T;
}

export const api = {
  health: () =>
    request<{
      ok: boolean;
      model: string;
      configured: boolean;
      authenticated: boolean;
      database: "mysql" | "local";
    }>("/api/health"),
  session: () =>
    request<{ authenticated: boolean; username?: string }>("/api/auth/session"),
  login: (username: string, password: string) =>
    request<{ authenticated: boolean; username: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () =>
    request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  lyrics: (payload: {
    prompt: string;
    title?: string;
    mode?: "write_full_song" | "edit";
    lyrics?: string;
  }) =>
    request<LyricsResult>("/api/lyrics", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  songTitle: (payload: {
    prompt: string;
    title?: string;
    lyrics?: string;
    instrumental?: boolean;
    excludedTitles?: string[];
    variation?: number;
  }) =>
    request<{ title: string; description: string }>("/api/song-title", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  music: (payload: {
    title: string;
    prompt: string;
    lyrics?: string;
    instrumental?: boolean;
    vocalStyle?: "female-clear" | "female-powerful" | "male-warm" | "male-deep";
  }) =>
    request<{ track: Track }>("/api/music", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  tracks: () => request<{ tracks: Track[] }>("/api/tracks"),
  publicTrack: (id: string) =>
    request<{ track: Track }>(`/api/public/tracks/${encodeURIComponent(id)}`),
  dashboard: () => request<DashboardStats>("/api/dashboard"),
  updateTrack: (id: string, payload: Partial<Track>) =>
    request<{ track: Track }>(`/api/tracks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  releaseDraft: (id: string) =>
    request<{ draft: NeteaseReleaseDraft }>(
      `/api/tracks/${encodeURIComponent(id)}/release-draft`,
    ),
  updateReleaseDraft: (
    id: string,
    payload: Partial<Omit<NeteaseReleaseDraft, "trackId" | "updatedAt">>,
  ) =>
    request<{ draft: NeteaseReleaseDraft }>(
      `/api/tracks/${encodeURIComponent(id)}/release-draft`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    ),
  recordPlay: (id: string) =>
    request<{ plays: number }>(`/api/tracks/${id}/play`, { method: "POST" }),
  mobileUsers: () => request<{ users: MobileUser[] }>("/api/mobile-users"),
  mobileGenerationRecords: (id: string) =>
    request<{ records: MobileGenerationRecord[] }>(
      `/api/mobile-users/${encodeURIComponent(id)}/generations`,
    ),
  updateMobileUser: (
    id: string,
    payload: { status?: MobileUser["status"]; quota?: number },
  ) =>
    request<{ user: MobileUser }>(`/api/mobile-users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  mobile: {
    session: () =>
      request<{ authenticated: boolean; user?: MobileUser }>("/api/m/session"),
    register: (username: string, password: string) =>
      request<{ user: MobileUser; message: string }>("/api/m/register", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    login: (username: string, password: string) =>
      request<{ user: MobileUser }>("/api/m/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    logout: () => request<{ ok: boolean }>("/api/m/logout", { method: "POST" }),
    lyrics: (payload: {
      prompt: string;
      title?: string;
      mode?: "write_full_song" | "edit";
      lyrics?: string;
    }) =>
      request<LyricsResult>("/api/m/lyrics", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    songTitle: (payload: {
      prompt: string;
      title?: string;
      lyrics?: string;
      instrumental?: boolean;
      excludedTitles?: string[];
      variation?: number;
    }) =>
      request<{ title: string; description: string }>("/api/m/song-title", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    music: (payload: {
      title: string;
      prompt: string;
      lyrics?: string;
      instrumental?: boolean;
      vocalStyle?: "female-clear" | "female-powerful" | "male-warm" | "male-deep";
    }) =>
      request<{ track: Track; remaining: number; user: MobileUser }>("/api/m/music", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  },
};
